import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Drawer,
  DrawerProps,
  Stack,
  SxProps,
  Theme,
  ThemeProvider,
  Typography
} from '@mui/material';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { environment } from '../../environment';
import { useTheme } from '../../theme';
import { IconButton } from './iconButton';

export const drawerModalLargeWidth = '800px';
export const drawerModalConnectionWidth = '618px';

interface VaultDrawerProps extends Omit<DrawerProps, 'title'> {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onRefresh?: () => void;
  readonly children: ReactNode;
  readonly title?: ReactNode;
  readonly sx?: SxProps<Theme>;
  readonly customAction?: ReactNode;
  /**
   * Width of the modal
   * */
  readonly width?: string;
}

export function DrawerModal({
  open,
  onClose,
  onRefresh,
  width = '562px',
  title,
  children,
  sx,
  customAction,
  ...props
}: VaultDrawerProps) {
  const theme = useTheme({ mode: 'light', isProd: environment.isProd });
  const paperRef = useRef<HTMLDivElement | null>(null);

  const closeDrawer = () => {
    const { activeElement } = document;
    const isFocusedInsideDrawer =
      activeElement instanceof HTMLElement && paperRef.current?.contains(activeElement) === true;

    if (isFocusedInsideDrawer) activeElement.blur();
    onClose();
  };

  return (
    <ThemeProvider theme={theme}>
      <Drawer
        anchor="right"
        open={open}
        onClose={closeDrawer}
        transitionDuration={{ enter: 300, exit: 100 }}
        PaperProps={{
          ref: paperRef,
          sx: {
            color: theme => theme.brandPalette.foreground.neutral,
            minWidth: { xs: '100%', md: width ?? '562px' },
            maxWidth: { xs: '100%', md: width ?? '562px' },
            width: { xs: '100%', md: width ?? '562px' },
            margin: theme => ({ xs: theme.spacing(0), md: theme.spacing(5) }),
            borderRadius: theme => ({ xs: `${theme.radius.none}`, md: `${theme.radius.l}` }),
            height: { xs: '100%', md: 'calc(100% - 32px)' },
            background: theme => theme.brandPalette.background.neutral,
            '&::-webkit-scrollbar': { display: 'none' },
            overflowX: 'hidden',
            ...sx
          }
        }}
        {...props}
      >
        {open && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            className="drawerHeader"
            sx={{
              padding: theme => theme.spacing(8, 8, 5, 8),
              position: 'sticky',
              top: '0px',
              background: theme => theme.brandPalette.background.neutral,
              zIndex: '999'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 400 }}>
              {title}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={4}>
              {customAction}
              {onRefresh && (
                <IconButton
                  aria-label="refresh"
                  variant="secondary"
                  onClick={onRefresh}
                  sx={{
                    color: theme => theme.brandPalette.foreground.neutral
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              )}
              <IconButton onClick={closeDrawer} icon="close" variant="secondary" />
            </Stack>
          </Stack>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>{children}</Box>
      </Drawer>
    </ThemeProvider>
  );
}

interface DrawerModalButtonsWrapperProps {
  readonly children: React.ReactNode;
  readonly disableBoxShadow?: boolean;
  readonly sx?: SxProps<Theme>;
}

export function DrawerModalButtonsWrapper({
  children,
  disableBoxShadow = false,
  sx
}: DrawerModalButtonsWrapperProps) {
  const [hasShadow, setHasShadow] = useState<boolean>(false);

  useEffect(() => {
    const parent = document.querySelector('.MuiDrawer-paper');
    if (parent == null) return;

    const handleScroll = () => {
      setHasShadow(parent.scrollTop > 0);
    };

    parent.addEventListener('scroll', handleScroll);
    return () => parent.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Stack
      direction="row"
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 1,
        background: theme => theme.brandPalette.background.neutral,
        boxShadow: !disableBoxShadow && hasShadow ? '0px -4px 12px rgba(0,0,0,0.08)' : 'none',
        ...sx
      }}
      width="100%"
      py={6}
      px={8}
      gap={4}
    >
      {children}
    </Stack>
  );
}
